/** Provider that picks and renders the highest-priority eligible onboarding hint. */
import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import OnboardingHintCard from '@/features/onboarding/components/OnboardingHintCard';
import { OnboardingHintQueueContext } from '@/features/onboarding/context/OnboardingHintQueueContext.def';
import { selectActiveOnboardingHint } from '@/features/onboarding/utils/selectActiveOnboardingHint';

export function OnboardingHintQueueProvider({ enabled, children }) {
  const [registrations, setRegistrations] = useState(
    () => new Map(),
  );
  const [sessionDismissed, setSessionDismissed] = useState(
    () => new Set(),
  );

  const setRegistration = useCallback((id, entry) => {
    setRegistrations((prev) => {
      const next = new Map(prev);
      if (!entry) {
        next.delete(id);
      } else {
        next.set(id, entry);
      }
      return next;
    });
  }, []);

  const activeId = useMemo(
    () =>
      enabled
        ? selectActiveOnboardingHint(registrations, sessionDismissed)
        : null,
    [enabled, registrations, sessionDismissed],
  );

  const dismissActive = useCallback(() => {
    if (!activeId) {
      return;
    }
    const entry = registrations.get(activeId);
    entry?.onDismiss?.();
    setSessionDismissed((prev) => {
      const next = new Set(prev);
      next.add(activeId);
      return next;
    });
  }, [activeId, registrations]);

  const activeEntry = activeId ? registrations.get(activeId) : null;

  const value = useMemo(
    () => ({
      enabled,
      activeId,
      setRegistration,
    }),
    [enabled, activeId, setRegistration],
  );

  return (
    <OnboardingHintQueueContext.Provider value={value}>
      {children}
      {enabled && activeEntry ? (
        <OnboardingHintCard
          body={activeEntry.body}
          title={activeEntry.title}
          testId={activeEntry.testId}
          onDismiss={dismissActive}
          dismissTabIndex={activeEntry.dismissTabIndex}
        />
      ) : null}
    </OnboardingHintQueueContext.Provider>
  );
}
